import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "img_url": "https://example.com/beach_scene.jpg",
 *     "prompt": "一个人在沙滩上奔跑，海浪拍打着岸边"
 *   },
 *   "parameters": {
 *     "resolution": "1080P",
 *     "duration": 5,
 *     "prompt_extend": true,
 *     "watermark": false,
 *     "audio": true,
 *     "seed": 12345,
 *     "negative_prompt": "模糊，低质量，抖动"
 *   }
 * }
 * ```
 */
export interface AlibabaWanx25I2vPreviewParams {
  /**
   * 输入参数，包含图片和提示词信息
   */
  input: {
    /**
     * 图片URL或Base64编码的图片数据（必填），系统会自动将URL转换为Base64格式
     */
    img_url: string;
    /**
     * 文本提示词（可选），描述要生成的视频内容，长度不超过2000字符，超过会自动截断
     */
    prompt?: string;
    /**
     * 自定义背景音乐URL（可选）
     */
    audio_url?: string;
  };
  /**
   * 生成参数（可选）
   */
  parameters?: {
    /**
     * 视频分辨率，必须是支持的分辨率之一，默认1080P
     * @default "1080P"
     * @values 480P | 720P | 1080P
     */
    resolution?: '480P' | '720P' | '1080P';
    /**
     * 视频时长（秒），支持5秒或10秒，默认5秒
     * @default 5
     * @values 5 | 10
     */
    duration?: 5 | 10;
    /**
     * 是否启用提示词扩展功能，启用后会自动优化和丰富提示词，默认true
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否在生成的视频上添加水印，默认false
     * @default false
     */
    watermark?: boolean;
    /**
     * 是否包含背景音频，默认true
     * @default true
     */
    audio?: boolean;
    /**
     * 随机种子，用于确保结果可复现，范围0-2147483647
     * @range 0 - 2147483647
     */
    seed?: number;
    /**
     * 负向提示词，描述不希望出现在视频中的内容，长度不超过500字符
     */
    negative_prompt?: string;
  };
}

export interface AlibabaWanx25I2vPreviewResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx25_i2v_preview
 *
 * <p>阿里巴巴 Wanx2.5 I2V Preview 图像到视频生成接口</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx25I2vPreview } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx25I2vPreview(
 *   {
 *     "input": {
 *       "img_url": "https://example.com/beach_scene.jpg",
 *       "prompt": "一个人在沙滩上奔跑，海浪拍打着岸边"
 *     },
 *     "parameters": {
 *       "resolution": "1080P",
 *       "duration": 5,
 *       "prompt_extend": true,
 *       "watermark": false,
 *       "audio": true,
 *       "seed": 12345,
 *       "negative_prompt": "模糊，低质量，抖动"
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx25I2vPreviewParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx25I2vPreview(
  params: AlibabaWanx25I2vPreviewParams,
): Promise<AlibabaWanx25I2vPreviewResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx25_i2v_preview',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx25I2vPreviewResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'video');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
